
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { FileText, Upload } from "lucide-react";

interface BenposUploaderProps {
  isProcessingFile: boolean;
  onFileUpload: (file: File) => void;
  selectedFile: File | null;
}

export const BenposUploader: React.FC<BenposUploaderProps> = ({ 
  isProcessingFile, 
  onFileUpload,
  selectedFile
}) => {
  const [dragActive, setDragActive] = useState(false);
  
  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };
  
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      validateAndUploadFile(e.dataTransfer.files[0]);
    }
  };
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      validateAndUploadFile(e.target.files[0]);
    }
  };
  
  const validateAndUploadFile = (file: File) => {
    // Check file type
    const validTypes = ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
    if (!validTypes.includes(file.type)) {
      toast.error("Invalid file format. Please upload a CSV or Excel file.");
      return;
    }
    
    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large. Maximum file size is 5MB.");
      return;
    }
    
    // Process the file
    toast.success("File uploaded successfully. Processing data...");
    onFileUpload(file);
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium">Upload BENPOS File</h3>
      <p className="text-sm text-muted-foreground">
        Upload the BENPOS file received from the RTA (Registrar and Transfer Agent) to visualize beneficiary details.
      </p>
      
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center ${
          dragActive ? "border-primary bg-primary/5" : "border-muted-foreground/20"
        } transition-colors`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <div className="flex flex-col items-center justify-center space-y-4">
          {isProcessingFile ? (
            <div className="flex flex-col items-center gap-2">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
              <p className="text-sm">Processing BENPOS file...</p>
            </div>
          ) : (
            <>
              <div className="rounded-full bg-primary/10 p-3">
                <Upload className="h-8 w-8 text-primary" />
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium">
                  Drag and drop your BENPOS file here
                </p>
                <p className="text-xs text-muted-foreground">
                  CSV or Excel files up to 5MB
                </p>
                
                <label htmlFor="benpos-upload" className="cursor-pointer">
                  <div className="mt-2 inline-flex text-sm text-primary hover:underline">
                    or browse files
                  </div>
                  <input
                    id="benpos-upload"
                    name="benpos-upload"
                    type="file"
                    className="sr-only"
                    accept=".csv,.xls,.xlsx"
                    onChange={handleFileChange}
                  />
                </label>
              </div>
            </>
          )}
        </div>
      </div>
      
      {selectedFile && !isProcessingFile && (
        <div className="bg-muted/50 rounded-lg p-4 flex items-center">
          <FileText className="h-5 w-5 text-muted-foreground mr-3" />
          <div className="flex-1">
            <p className="text-sm font-medium">{selectedFile.name}</p>
            <p className="text-xs text-muted-foreground">
              {(selectedFile.size / 1024).toFixed(1)}KB • Uploaded {new Date().toLocaleTimeString()}
            </p>
          </div>
          <Button variant="outline" size="sm">
            Replace
          </Button>
        </div>
      )}
    </div>
  );
};
